import styled from "@emotion/styled";
import type { ReactNode } from "react";

import { MDSText, MDSTextColor, MDSTextVariant } from "@/mds";
import {
  AudioRecordingCard,
  AudioRecordingCardBody,
  AudioRecordingCardHeader,
} from "@/pages/audio-recordings/shared/audio-recording-card";
import type { LocalAudioArtifactDebugData } from "@/pages/audio-recordings/shared/use-local-audio-artifact-debug-data";

type CapturedAudioArtifactCardProps = {
  data: LocalAudioArtifactDebugData | null;
  emptyMessage?: string;
  error?: string | null;
  headerActions?: ReactNode;
  headerTitle?: string;
  isLoading?: boolean;
};

export const CapturedAudioArtifactCard = ({
  data,
  emptyMessage,
  error,
  headerActions,
  headerTitle,
  isLoading = false,
}: CapturedAudioArtifactCardProps) => {
  const resolvedEmptyMessage = emptyMessage ?? data?.emptyMessage ?? "No local artifact data yet.";
  const resolvedHeaderTitle = headerTitle ?? data?.artifactLabel ?? "Local Audio Artifact";

  return (
    <AudioRecordingCard>
      <AudioRecordingCardHeader title={resolvedHeaderTitle} actions={headerActions} />
      <AudioRecordingCardBody>
        <SectionHeader>
          <MDSText variant={MDSTextVariant.TextSm}>
            {data?.artifactLabel ?? "Local Audio Artifact"}
          </MDSText>
          {data ? (
            <StatsRow>
              <StatBadge variant={MDSTextVariant.TextXs} color={MDSTextColor.Secondary}>
                State: {data.artifactStateLabel}
              </StatBadge>
              <StatBadge variant={MDSTextVariant.TextXs} color={MDSTextColor.Secondary}>
                {data.unitLabel}: {data.unitCount}
              </StatBadge>
              <StatBadge variant={MDSTextVariant.TextXs} color={MDSTextColor.Secondary}>
                Duration: {data.totalDurationLabel}
              </StatBadge>
              <StatBadge variant={MDSTextVariant.TextXs} color={MDSTextColor.Secondary}>
                Size: {data.totalSizeLabel}
              </StatBadge>
            </StatsRow>
          ) : null}
        </SectionHeader>

        {data?.mimeType ? (
          <MetadataText variant={MDSTextVariant.TextXs} color={MDSTextColor.Secondary}>
            MIME type: {data.mimeType}
          </MetadataText>
        ) : null}

        {data?.filePath ? (
          <MetadataText variant={MDSTextVariant.TextXs} color={MDSTextColor.Secondary}>
            File path: {data.filePath}
          </MetadataText>
        ) : null}

        <ScrollableContent>
          {error ? (
            <MDSText variant={MDSTextVariant.TextXs} color={MDSTextColor.Secondary}>
              {error}
            </MDSText>
          ) : isLoading ? (
            <MDSText variant={MDSTextVariant.TextXs} color={MDSTextColor.Secondary}>
              Loading local artifact data…
            </MDSText>
          ) : data && data.units.length > 0 ? (
            <ArtifactList>
              {data.units.map(unit => (
                <ArtifactListItem key={unit.id}>
                  <MDSText variant={MDSTextVariant.TextXs}>{unit.detail}</MDSText>
                </ArtifactListItem>
              ))}
            </ArtifactList>
          ) : (
            <MDSText variant={MDSTextVariant.TextXs} color={MDSTextColor.Secondary}>
              {resolvedEmptyMessage}
            </MDSText>
          )}
        </ScrollableContent>
      </AudioRecordingCardBody>
    </AudioRecordingCard>
  );
};

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const StatsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const StatBadge = styled(MDSText)`
  padding: 4px 10px;
  border-radius: 999px;
  background-color: ${({ theme }) => theme.colors.background.badge.default};
`;

const MetadataText = styled(MDSText)`
  word-break: break-word;
`;

const ScrollableContent = styled.div`
  max-height: 160px;
  overflow-y: auto;
  padding-right: 8px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ArtifactList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const ArtifactListItem = styled.div`
  padding: 0.5rem 0.75rem;
  border-radius: 0.5rem;
  background-color: ${({ theme }) => theme.colors.background.card.default};
`;
